import React, { useState, useRef, useEffect } from 'react';
import { Camera, User, Loader2, Check, X, RefreshCw } from 'lucide-react';
import { UserProfile } from '../../types';
import { db, storage, auth } from '../../services/firebase'; 
import { collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; 

interface ProfileSetupProps {
  profile: UserProfile;
  onComplete: (updates: Partial<UserProfile>) => void;
}

const RESERVED_USERNAMES = ['user', 'admin', 'administrator', 'scoop', 'support', 'official', 'null', 'undefined', 'test'];

const compressImage = (file: File, maxWidth = 800, quality = 0.85): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error('Compression failed'));
                }, 'image/jpeg', quality);
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};

export const ProfileSetup: React.FC<ProfileSetupProps> = ({ profile, onComplete }) => {
  // Start completely blank, the Super-Sync will fill it instantly
  const [username, setUsername] = useState('');
  const [avatar, setAvatar] = useState('');
  const [bio, setBio] = useState('');
  const [hasAutoFilled, setHasAutoFilled] = useState(false);
  
  const [imageFile, setImageFile] = useState<File | null>(null); 
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // 🟢 THE SUPER-SYNC: Bypasses DB lag by pulling straight from Google Auth
  // ============================================================================
  useEffect(() => {
    const googleUser = auth.currentUser;
    
    // Grab the best available data from either the database OR the live Google session
    const bestName = profile?.displayName || (profile as any)?.name || googleUser?.displayName || '';
    const bestAvatar = profile?.avatar || (profile as any)?.photoURL || googleUser?.photoURL || '';
    const bestBio = profile?.bio || '';

    // If the data arrives, snap it into the UI immediately
    if (bestAvatar && !avatar) setAvatar(bestAvatar);
    
    // Safe-catch: Only auto-fill the bio if the user hasn't already started typing their own
    if (bestBio && !bio) setBio(bestBio);

    // Auto-fill the username from Google
    if (bestName && !hasAutoFilled) {
      const cleanName = bestName.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 15);
      if (cleanName) {
        setUsername(cleanName);
        setHasAutoFilled(true); // Ensures we only auto-fill once so we don't annoy the user
      }
    }
  }, [profile, avatar, bio, hasAutoFilled]);

  // ============================================================================
  // 🟢 USERNAME DEBOUNCE VALIDATION
  // ============================================================================
  useEffect(() => {
    if (!username) {
      setUsernameAvailable(false);
      setUsernameError('');
      setCheckingUsername(false);
      return;
    }

    if (username.length < 3) {
      setUsernameAvailable(false);
      setUsernameError('Must be at least 3 characters');
      setCheckingUsername(false);
      return;
    }

    if (!/^[a-z0-9_]+$/.test(username)) {
      setUsernameAvailable(false);
      setUsernameError('Only letters, numbers, and underscores');
      setCheckingUsername(false);
      return;
    }

    if (RESERVED_USERNAMES.includes(username)) {
      setUsernameAvailable(false);
      setUsernameError('This username is reserved or invalid');
      setCheckingUsername(false);
      return;
    }

    setCheckingUsername(true);
    setUsernameError('');
    setUsernameAvailable(false);

    const timer = setTimeout(() => {
      const checkDb = async () => {
        try {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('handle', '==', `@${username}`));
          const querySnapshot = await getDocs(q);
          
          if (querySnapshot.empty) {
            setUsernameAvailable(true); 
            setUsernameError('');
          } else {
            setUsernameError(`@${username} is already taken`);
            setUsernameAvailable(false);
            generateUsernameSuggestions(username);
          }
        } catch (error) {
          console.error('Error checking username:', error);
          setUsernameError('Connection error. Try again.');
          setUsernameAvailable(false); 
        } finally {
          setCheckingUsername(false);
        }
      };
      checkDb();
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    setUploading(true);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatar(reader.result as string);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const generateUsernameSuggestions = (baseName: string) => {
    const suggestionsList: string[] = [];
    for (let i = 1; i <= 5; i++) {
        const randomNum = Math.floor(Math.random() * 999);
        suggestionsList.push(`${baseName}${randomNum}`);
    }
    setSuggestions(suggestionsList);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(value); 
    setSuggestions([]);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setUsername(suggestion);
    setSuggestions([]);
  };

  const handleGenerateNewSuggestions = () => {
    generateUsernameSuggestions(username);
  };

  // ============================================================================
  // 🟢 FIXED: EXPLICIT ERROR CATCHING FOR SILENT DATABASE FAILURES
  // ============================================================================
  const handleSave = async () => {
    if (!usernameAvailable || !username || RESERVED_USERNAMES.includes(username)) {
        return;
    }

    setLoading(true);
    
    try {
      let finalAvatar = avatar;
      
      // 1. Handle Image Upload
      if (imageFile) {
        try {
          const compressedBlob = await compressImage(imageFile, 800, 0.85);
          const storageRef = ref(storage, `avatars/${profile.uid}/${Date.now()}_compressed.jpg`);
          const snapshot = await uploadBytes(storageRef, compressedBlob);
          finalAvatar = await getDownloadURL(snapshot.ref);
        } catch (storageErr) {
          console.error("Image upload failed, using fallback", storageErr);
        }
      }
      
      // 2. Set Fallback if empty
      if (!finalAvatar) {
        finalAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random&color=fff&size=128`;
      }

      // 3. Send to Database
      // Await is crucial here to ensure the backend confirms the write
      await onComplete({ 
        bio, 
        handle: `@${username}`, 
        avatar: finalAvatar, 
        hasSetupProfile: true 
      });
      
    } catch (error: any) { 
      // 4. CATCH AND DISPLAY THE ERROR
      console.error("Profile update error:", error);
      // Alert the user so they know *why* it failed (e.g., permissions, network)
      alert(`Transmission Failed: ${error.message || 'Check connection or database permissions.'}`);
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex bg-black text-white overflow-hidden">
      
      <div className="hidden lg:flex relative w-[60%] border-r border-white/20 bg-zinc-900 items-center justify-center p-16 overflow-hidden">
         <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950 to-zinc-900" />
         
         <div className="relative z-10 w-full max-w-2xl">
            <span className="text-white/70 font-mono tracking-widest uppercase text-xs mb-4 block">System Initialization</span>
            <h1 className="text-[8rem] xl:text-[10rem] leading-[0.8] font-black italic tracking-tighter uppercase text-white mb-6">
              Establish<br/>Identity.
            </h1>
            <p className="text-xl font-bold uppercase tracking-widest text-zinc-400 max-w-md leading-relaxed border-l-2 border-white pl-6">
              The streets are watching. Claim your handle.
            </p>
         </div>
      </div>

      <div className="w-full lg:w-[40%] flex flex-col h-full overflow-y-auto relative bg-black">
          
          <div className="flex-1 flex flex-col justify-center p-8 lg:px-12 lg:py-12 max-w-xl mx-auto w-full animate-in fade-in duration-500">
            
            <div className="lg:hidden mt-8 mb-10 border-b border-zinc-800 pb-8">
              <h2 className="text-4xl font-black text-white uppercase italic tracking-tighter leading-none mb-3">
                Initialize<br/>Identity
              </h2>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">
                Establish your presence.
              </p>
            </div>
            
            <div className="flex flex-col mb-10">
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3 block">
                Avatar
              </label>
              <div className="flex items-end gap-6">
                  <div 
                      className="relative group w-32 h-32 cursor-pointer bg-zinc-950 border border-zinc-800 flex items-center justify-center overflow-hidden hover:border-white transition-colors rounded-full lg:rounded-none shadow-lg shrink-0" 
                      onClick={handleAvatarClick}
                  >
                    {avatar ? (
                      <img 
                        src={avatar} 
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" 
                        alt="Avatar" 
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(username || 'User')}&background=random&color=fff&size=128`;
                        }}
                      />
                    ) : (
                      <User size={48} className="text-zinc-800 group-hover:text-zinc-600 transition-colors" />
                    )}
                    
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                      {uploading ? (
                        <Loader2 className="animate-spin text-white" size={24} />
                      ) : (
                        <Camera size={24} className="text-white" />
                      )}
                    </div>
                    
                    <input 
                      ref={fileInputRef}
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleFileUpload} 
                      disabled={uploading}
                    />
                  </div>
                  
                  <div className="pb-2">
                      <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                        {uploading ? 'UPLOADING...' : '1:1 IMAGE REQUIRED'}
                      </p>
                      <button 
                        onClick={handleAvatarClick} 
                        className="text-xs font-black uppercase tracking-widest text-white mt-2 hover:underline underline-offset-4"
                      >
                        UPLOAD IMAGE
                      </button>
                  </div>
              </div>
            </div>

            <div className="mb-8">
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3 block">
                  Username
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-4 text-zinc-600 font-mono font-bold">@</span>
                <input 
                  type="text" 
                  value={username}
                  onChange={handleUsernameChange}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl lg:rounded-none p-4 pl-10 text-white font-mono text-base outline-none focus:border-white transition-colors placeholder:uppercase placeholder:font-sans placeholder:font-black placeholder:tracking-wider shadow-sm lg:shadow-none"
                  placeholder="USERNAME"
                  maxLength={15}
                />
                {checkingUsername && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <Loader2 className="animate-spin text-zinc-500" size={20} />
                  </div>
                )}
                {usernameAvailable && !checkingUsername && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <Check className="text-white" size={20} />
                  </div>
                )}
                {usernameError && !checkingUsername && !usernameAvailable && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <X className="text-white" size={20} />
                  </div>
                )}
              </div>
              
              {usernameError && <p className="text-white text-[10px] font-mono uppercase tracking-widest mt-3 border border-zinc-800 rounded-xl lg:rounded-none bg-zinc-950 p-2 inline-block">ERR: {usernameError}</p>}
              {usernameAvailable && !checkingUsername && <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest mt-3 pl-2">STATUS: AVAILABLE</p>}
              
              {suggestions.length > 0 && (
                <div className="mt-6 border border-zinc-800 rounded-2xl lg:rounded-none bg-zinc-950 p-4 shadow-sm lg:shadow-none">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest">Alternates:</p>
                    <button onClick={handleGenerateNewSuggestions} className="text-zinc-500 hover:text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1 transition-colors">
                      <RefreshCw size={12} /> Refresh
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className="px-4 py-2 bg-black border border-zinc-800 rounded-full lg:rounded-none text-white text-[10px] font-mono hover:bg-white hover:text-black hover:border-white transition-all shadow-sm lg:shadow-none"
                      >
                        @{suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-12">
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3 block">
                  Bio
              </label>
              <textarea 
                value={bio} 
                onChange={(e) => setBio(e.target.value)} 
                className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl lg:rounded-none p-4 text-white font-mono text-base leading-relaxed outline-none focus:border-white h-32 resize-none transition-colors shadow-sm lg:shadow-none" 
                placeholder="TRANSMIT THOUGHTS..." 
                maxLength={200}
              />
              <div className="flex justify-between items-center mt-3 px-2 lg:px-0">
                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">
                      Keep it strictly culture.
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">
                      {bio.length} / 200
                  </span>
              </div>
            </div>
            
            <div className="mt-auto pt-8 border-t border-zinc-800 pb-safe">
              <button 
                onClick={handleSave} 
                disabled={uploading || !usernameAvailable || checkingUsername || loading}
                className={`w-full py-5 font-black uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center rounded-full lg:rounded-none ${
                  uploading || !usernameAvailable || checkingUsername || loading
                  ? 'bg-zinc-950 text-zinc-600 cursor-not-allowed border border-zinc-800' 
                  : 'bg-white text-black hover:bg-zinc-200 shadow-xl lg:shadow-none hover:shadow-white/10'
                }`}
              >
                {loading ? 'Transmitting...' : 'Establish Identity'}
              </button>
            </div>

          </div>
      </div>
    </div>
  );
};